import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createCaseStep, updateCaseStep } from "@/features/cases/api";
import type { CaseStep } from "@/features/cases/types";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { applyApiErrors } from "@/lib/form-errors";

interface TaskFormValues {
  description: string;
  dueDate: string;
}

const validationSchema = Yup.object({
  description: Yup.string().trim().required("A task description is required"),
});

interface TaskFormDialogProps {
  caseId: string;
  /** `null` = create a new task; otherwise edit this one. */
  step: CaseStep | null;
  /** Position to give a newly-created task (append to the end). */
  nextPosition: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Create/edit a single task (a case workflow step): description + due date. */
export function TaskFormDialog({
  caseId,
  step,
  nextPosition,
  open,
  onOpenChange,
  onSaved,
}: TaskFormDialogProps) {
  const editing = step !== null;

  async function handleSubmit(values: TaskFormValues, bag: FormikHelpers<TaskFormValues>) {
    const payload = {
      description: values.description.trim(),
      due_date: values.dueDate || null,
    };
    try {
      if (editing) {
        await updateCaseStep(caseId, step.id, payload);
      } else {
        await createCaseStep(caseId, { ...payload, position: nextPosition });
      }
      toast.success(editing ? "Task updated" : "Task added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save task." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Task" : "Add Task"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={{ description: step?.description ?? "", dueDate: step?.dueDate ?? "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField
                name="description"
                label="Task"
                labelClassName={FIELD_LABEL}
                placeholder="e.g. Request wage & income transcripts"
              />
              <FormField name="dueDate" label="Due Date" labelClassName={FIELD_LABEL} type="date" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Add Task"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
